/**
 * Emisor de eventos de producto hacia GA4 + PostHog.
 *
 * La implementación real vive en `public/analytics.js`, que se carga desde
 * index.html antes que el bundle y expone `window.okeyTrack`. Este módulo es
 * solo el acceso tipado, con un no-op de seguridad por si analytics.js no
 * llegara a cargar (bloqueador, fallo de red).
 *
 * REGLA: los parámetros son SIEMPRE metadatos. Nunca email, nombre, contraseña,
 * identificadores personales ni contenido de documentos. La analítica de
 * producto con PII va por `trackEvent()` contra nuestro backend, no por aquí.
 */

export type TrackParams = Record<string, string | number | boolean>;

export function okeyTrack(event: string, params: TrackParams = {}): void {
  const fn = (window as unknown as { okeyTrack?: (e: string, p: TrackParams) => void }).okeyTrack;
  if (typeof fn === 'function') {
    fn(event, params);
  }
}

/**
 * Emite un evento COMO MUCHO UNA VEZ por clave.
 *
 * Necesario para sign_up. La pantalla de callback se ejecuta cada vez que se
 * carga /callback, y con `persistSession: false` + `detectSessionInUrl: true`
 * cada carga vuelve a resolver la sesión desde la URL y vuelve a considerar la
 * cuenta "recién creada" mientras esté dentro de la ventana de 5 minutos. Sin
 * guarda, un solo alta emite varios sign_up: medido en producción el
 * 2026-07-27, 4 eventos para una única cuenta, más sign_up (4) que
 * signup_started (3), lo que es imposible si cada alta requiere un intento.
 *
 * La marca va en localStorage y no en memoria porque la duplicación ocurre
 * entre cargas de página distintas, no dentro de una misma.
 *
 * Falla en abierto: si localStorage no está disponible (modo privado, cuota),
 * se emite el evento. Es preferible contar de más una vez a perder la
 * conversión entera.
 */
export function okeyTrackOnce(key: string, event: string, params: TrackParams = {}): boolean {
  const storageKey = `okey_evt_once:${event}:${key}`;
  try {
    if (window.localStorage.getItem(storageKey)) return false;
    window.localStorage.setItem(storageKey, '1');
  } catch {
    /* sin almacenamiento: se emite igualmente */
  }
  okeyTrack(event, params);
  return true;
}
